import { useEffect, useState, type KeyboardEvent } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import {
  ANDROID_CHROME_PLAY_STORE_URL,
  shouldSuggestChromeOnAndroidForPush,
} from '../lib/deviceDetection';
import { MAIN_APP_BELOW_TOP_BAR_CLASS } from '../lib/layoutChrome';
import { AppButton } from './ui/AppButton';

const PROMPT_DISMISSED_STORAGE_KEY = 'fdh-android-chrome-push-hint-dismissed';

const isPromptDismissed = (): boolean => {
  try {
    return localStorage.getItem(PROMPT_DISMISSED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const markPromptDismissed = (): void => {
  try {
    localStorage.setItem(PROMPT_DISMISSED_STORAGE_KEY, 'true');
  } catch {
    // ignore localStorage failures
  }
};

export const AndroidChromePushHintPrompt = () => {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const shouldShow = shouldSuggestChromeOnAndroidForPush() && !isPromptDismissed();
    setVisible(shouldShow);
  }, []);

  const handleDismiss = () => {
    markPromptDismissed();
    setVisible(false);
  };

  const handleOpenChromeStore = () => {
    window.open(ANDROID_CHROME_PLAY_STORE_URL, '_blank', 'noopener,noreferrer');
  };

  const handleKeyOpenChromeStore = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleOpenChromeStore();
    }
  };

  const handleKeyDismiss = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleDismiss();
    }
  };

  if (!visible) {
    return null;
  }

  return (
    <div className={`mx-auto w-full max-w-lg shrink-0 px-4 ${MAIN_APP_BELOW_TOP_BAR_CLASS}`}>
      <div
        className="rounded-xl border border-amber-200 bg-amber-50 p-3 shadow-sm dark:border-amber-800/60 dark:bg-amber-950/40"
        role="region"
        aria-label={t('settings.androidChromePushHintTitle')}
      >
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {t('settings.androidChromePushHintTitle')}
        </p>
        <p className="mt-1 text-xs text-gray-700 dark:text-gray-300">
          {t('settings.androidChromePushHintBody')}
        </p>
        <div className="mt-3 flex items-center justify-end gap-2">
          <AppButton
            type="button"
            variant="secondary"
            onClick={handleDismiss}
            onKeyDown={handleKeyDismiss}
            tabIndex={0}
            aria-label={t('settings.androidChromePushHintDismiss')}
            className="min-h-9 px-3 py-1.5 text-xs"
          >
            {t('settings.androidChromePushHintDismiss')}
          </AppButton>
          <AppButton
            type="button"
            variant="primary"
            onClick={handleOpenChromeStore}
            onKeyDown={handleKeyOpenChromeStore}
            tabIndex={0}
            aria-label={t('settings.androidChromePushHintCta')}
            className="min-h-9 px-3 py-1.5 text-xs"
          >
            {t('settings.androidChromePushHintCta')}
          </AppButton>
        </div>
      </div>
    </div>
  );
};
